import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import { Pen, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';

const EditAssetModal = ({ assetId, onClose, onSuccess, assetType }) => {
  const [form, setForm] = useState({
    name: '',
    alias: '',
    status: '',
    category: '',
    strategy: '',
    kind: '',
    locked: false,
    apiLocked: false,
    subtype: '',
    sector: '',
    ticker: '',
    symbolId: '',
    symbolType: '',
    currency: '',
    exchange: '',
    country: '',
  });

  const { addToast } = useToast();

  const isEquity = assetType === 'equities';
  const isOffshoreSymbol = assetType === 'offshore_symbols';

  useEffect(() => {
    fetch(`/app/analyst/assets/${assetId}?tab=${assetType}`)
      .then((res) => res.json())
      .then((data) => {
        setForm((prev) => ({
          ...prev,
          ...camelizeKeys(data),
        }));
      })
      .catch((err) => {
        console.error('Erro ao carregar ativo', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados do ativo' });
      });
  }, [assetId, assetType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const assetPayload = isOffshoreSymbol
      ? {
          alias: form.alias,
          strategy: form.strategy,
        }
      : form;

    const payload = snakecaseKeys({ asset: assetPayload });

    try {
      const res = await fetchWithCsrf(`/app/analyst/assets/${assetId}?tab=${assetType}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao atualizar ativo');
      }

      onClose();
      addToast({ type: 'success', message: 'Ativo atualizado com sucesso!' });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const strategyOptions = {
    investment_funds: [
      { label: '-', value: '-' },
      { label: 'Pós', value: 'Pós' },
      { label: 'Pré', value: 'Pré' },
      { label: 'IPCA+', value: 'IPCA+' },
      { label: 'Brasil', value: 'Brasil' },
      { label: 'Internacional', value: 'Internacional' },
    ],
    pension: [
      { label: 'Small Caps', value: 'Small Caps' },
      { label: 'Crédito Estruturado', value: 'Crédito Estruturado' },
      { label: 'Crédito Pós', value: 'Crédito Pós' },
      { label: 'Long Only', value: 'Long Only' },
      { label: 'Inflação', value: 'Inflação' },
      { label: 'Ativo', value: 'Ativo' },
      { label: 'Long Short', value: 'Long Short' },
      { label: 'Pós-fixado', value: 'Pós-fixado' },
      { label: 'Pré-fixado', value: 'Pré-fixado' },
      { label: 'Crédito High Yield', value: 'Crédito High Yield' },
      { label: 'Long Biased', value: 'Long Biased' },
      { label: 'Investimento no Exterior', value: 'Investimento no Exterior' },
      { label: 'Macro', value: 'Macro' },
      { label: 'Criptoativo', value: 'Criptoativo' },
      { label: 'Temáticos', value: 'Temáticos' },
      { label: 'Imobiliário', value: 'Imobiliário' },
      { label: '-', value: '-' },
    ],
    equities: [
      { label: 'Brasil', value: 'Brasil' },
      { label: 'Internacional', value: 'Internacional' },
    ],
    offshore_symbols: [
      { label: 'Renda Fixa Longo Prazo', value: 'Renda Fixa Longo Prazo' },
      { label: 'Renda Fixa Curto Prazo', value: 'Renda Fixa Curto Prazo' },
      { label: 'REITs', value: 'REITs' },
      { label: 'Commodities', value: 'Commodities' },
      { label: 'Ações', value: 'Ações' },
      { label: 'Caixa', value: 'Caixa' },
    ],
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Pen size={24} className="text-tealMedium" strokeWidth={1.25} />
            Editar Ativo
          </div>

          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <div className="border-t border-b border-bluePale py-4 mb-6">
          <p className="font-jost text-lg text-blueDarker">
            <span className="font-semibold">Ativo:</span> {form.displayName || form.name}
          </p>

          {isEquity && (
            <p className="font-jost text-lg text-blueDarker">
              <span className="font-semibold">Ticker:</span> {form.ticker}
            </p>
          )}

          {isOffshoreSymbol && (
            <>
              <p className="font-jost text-lg text-blueDarker">
                <span className="font-semibold">Ticker:</span> {form.ticker || '-'}
              </p>

              <p className="font-jost text-lg text-blueDarker">
                <span className="font-semibold">Símbolo:</span> {form.symbolId || '-'}
              </p>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          {isOffshoreSymbol ? (
            <>
              <Input name="alias" label="Nome de exibição" value={form.alias || ''} onChange={handleChange} />

              <Select
                name="strategy"
                label="Estratégia"
                value={form.strategy}
                onChange={handleChange}
                options={strategyOptions[assetType] || []}
                required
              />
            </>
          ) : (
            <>
              <Input name="alias" label="Nome de exibição" value={form.alias || ''} onChange={handleChange} />

              <Select
                name="status"
                label="Status"
                value={form.status}
                onChange={handleChange}
                options={[
                  { label: 'Ativo', value: 'active' },
                  { label: 'Fechado p/ captação', value: 'closed' },
                  { label: 'Em liquidação', value: 'sale' },
                ]}
                required
              />

              {isEquity ? (
                <Select
                  name="kind"
                  label="Tipo (BTG)"
                  value={form.kind}
                  onChange={handleChange}
                  options={[
                    { label: 'Ação', value: 'stock' },
                    { label: 'FII', value: 'fii' },
                  ]}
                  required
                />
              ) : (
                <Select
                  name="category"
                  label="Categoria"
                  value={form.category}
                  onChange={handleChange}
                  options={[
                    { label: 'Renda Fixa', value: 'Renda Fixa' },
                    { label: 'Multimercado', value: 'Multimercado' },
                    { label: 'Ações', value: 'Ações' },
                    { label: 'Alternativos', value: 'Alternativos' },
                  ]}
                  required
                />
              )}

              <Select
                name="strategy"
                label="Estratégia"
                value={form.strategy}
                onChange={handleChange}
                options={strategyOptions[assetType] || []}
                required
              />

              {isEquity && (
                <Select
                  name="subtype"
                  label="Subtipo"
                  value={form.subtype}
                  onChange={handleChange}
                  options={[
                    { label: 'Ação', value: 'stock' },
                    { label: 'FII', value: 'fii' },
                    { label: 'Fiagro', value: 'fiagro' },
                    { label: 'ETF', value: 'etf' },
                    { label: 'BDR', value: 'bdr' },
                    { label: 'ETF RF', value: 'etf_rf' },
                  ]}
                  required
                />
              )}

              {isEquity && (
                <Select
                  name="sector"
                  label="Setor"
                  value={form.sector}
                  onChange={handleChange}
                  options={[
                    { label: 'Comércio Varejista', value: 'Retail Trade' },
                    { label: 'Mineração de Energia', value: 'Energy Minerals' },
                    { label: 'Serviços de Saúde', value: 'Health Services' },
                    { label: 'Utilidades', value: 'Utilities' },
                    { label: 'Finanças', value: 'Finance' },
                    { label: 'Serviços ao Consumidor', value: 'Consumer Services' },
                    { label: 'Bens de Consumo Não Duráveis', value: 'Consumer Non-Durables' },
                    { label: 'Mineração Não Energética', value: 'Non-Energy Minerals' },
                    { label: 'Serviços Comerciais', value: 'Commercial Services' },
                    { label: 'Serviços de Distribuição', value: 'Distribution Services' },
                    { label: 'Transporte', value: 'Transportation' },
                    { label: 'Serviços de Tecnologia', value: 'Technology Services' },
                    { label: 'Indústrias de Processo', value: 'Process Industries' },
                    { label: 'Comunicações', value: 'Communications' },
                    { label: 'Fabricação de Produtores', value: 'Producer Manufacturing' },
                    { label: 'Diversos', value: 'Miscellaneous' },
                    { label: 'Tecnologia Eletrônica', value: 'Electronic Technology' },
                    { label: 'Serviços Industriais', value: 'Industrial Services' },
                    { label: 'Tecnologia de Saúde', value: 'Health Technology' },
                    { label: 'Bens de Consumo Duráveis', value: 'Consumer Durables' },
                  ]}
                  required
                />
              )}

              <CustomCheckbox
                labelClassName="text-base"
                name="apiLocked"
                label="Travar atualização via API / Importação"
                checked={form.apiLocked}
                onChange={() => handleCheckbox('apiLocked')}
              />
            </>
          )}

          <div className="flex justify-end items-center mt-1 md:col-span-2">
            <div className="flex justify-between gap-2">
              <SecondaryButton onClick={onClose}>CANCELAR</SecondaryButton>
              <Button type="submit">SALVAR ALTERAÇÕES</Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditAssetModal;
